import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FileText, ArrowRight, X } from '@phosphor-icons/react';
import { useTenantPath } from '../lib/tenantPath';
import useAuthStore from '../store/authStore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/*
 * iter132.12 — Amber ribbon that nudges the Tender Executive (and Managers)
 * to fill in the four canonical doc slots (Tender Document, EMD Page,
 * Participation Criteria, BOQ Sheet) on any bulk-uploaded tender still
 * carrying gaps.
 *
 * Self-hides when:
 *   - the caller doesn't have doc-upload permission (only TE + Managers do)
 *   - the summary endpoint returns zero missing rows
 *   - the user manually dismisses (session-scoped)
 *
 * Placed in Layout below the ReviewReminderBanner (both are amber; the
 * order is stable so the eye can predict where each cue lives).
 */
export default function TenderDocsBanner() {
  const { user } = useAuthStore();
  const nav = useNavigate();
  const tp = useTenantPath();
  const [summary, setSummary] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const canSee = ['Tender Executive', 'National Sales Manager', 'Vice President', 'President']
    .includes(user?.role);

  const load = useCallback(async () => {
    if (!user || user.impersonated_by || !canSee) return;
    try {
      const r = await axios.get(`${API}/tenders/summary/missing-docs`);
      setSummary(r.data);
    } catch {
      setSummary(null);
    }
  }, [user, canSee]);

  useEffect(() => {
    load();
    // Refresh every 3 minutes so the banner disappears the moment the last
    // slot is filled without waiting for a manual reload.
    const t = setInterval(load, 3 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  if (!canSee || dismissed) return null;
  if (!summary || summary.tender_count === 0) return null;

  const { tender_count, total_missing, items } = summary;
  const firstFew = items.slice(0, 3);

  return (
    <div
      className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between gap-3"
      data-testid="tender-docs-banner"
    >
      <div className="flex items-center gap-3 min-w-0">
        <FileText size={18} weight="bold" className="text-amber-700 shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-amber-900 truncate">
            {tender_count} tender{tender_count === 1 ? '' : 's'} missing {total_missing} document{total_missing === 1 ? '' : 's'}
          </div>
          <div className="text-xs text-amber-800 truncate hidden sm:block">
            {firstFew.map((it) => `${it.tender_number || it.buyer_name} (${it.missing_count})`).join(' · ')}
            {items.length > firstFew.length ? ` · +${items.length - firstFew.length} more` : ''}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          type="button"
          onClick={() => nav(tp(`/tenders/${items[0].id}`))}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-semibold bg-amber-700 text-white hover:bg-amber-800 transition"
          data-testid="tender-docs-banner-cta"
          title={`Open ${items[0].tender_number || items[0].buyer_name}`}
        >
          Fill missing <ArrowRight size={12} weight="bold" />
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="text-amber-800 hover:text-amber-950 p-1"
          aria-label="Dismiss"
          data-testid="tender-docs-banner-dismiss"
        >
          <X size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}
